import { useEffect, useRef, useState } from "react";
import { TileLayer } from "react-leaflet";
import { Layers, ChevronDown, Check } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Shared Leaflet basemap catalogue + controls, so the main Scan Map and the
 * per-officer scan-history map offer the SAME set of map styles and the same
 * picker. One source of truth: add a style here and it appears in both places.
 */

// Provider attribution intentionally not rendered (client preference,
// Sep 2 2026). NOTE: OSM/Esri/TomTom tile terms generally require
// visible attribution — to restore it, put the provider credits back
// here and re-enable attributionControl on the MapContainers.
const ATTR_NONE = "";

type MapTileVariant = {
  url: string;
  attribution: string;
  // Highest zoom the provider actually serves; Leaflet upscales beyond it so
  // the map never goes blank when a user zooms past the provider's coverage.
  maxNativeZoom?: number;
};
export type MapStyle = {
  name: string;
  light: MapTileVariant;
  dark: MapTileVariant;
  // Optional transparent overlays drawn on top of the base tiles — the Hybrid
  // style stacks road + place-label reference tiles over the satellite imagery.
  overlays?: string[];
};

export const MAP_STYLES: MapStyle[] = [
  // CARTO's free anonymous basemaps were shut off (tiles now render an
  // "API KEY REQUIRED" watermark), so the three CARTO styles below were
  // swapped for keyless Esri equivalents on Aug 31 2026. List length and
  // order are preserved so users' saved style indexes still point at the
  // same kind of map they chose.
  {
    name: "Canvas",
    light: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE, maxNativeZoom: 16 },
    dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE, maxNativeZoom: 16 },
  },
  {
    name: "Standard",
    light: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: ATTR_NONE },
    dark: { url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", attribution: ATTR_NONE },
  },
  {
    name: "Streets",
    light: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
    dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
  },
  {
    // Was "Positron" (a light-on-light CARTO duplicate of CartoDB); now the
    // light canvas in both themes, mirroring the original's behavior.
    name: "Light",
    light: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE, maxNativeZoom: 16 },
    dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE, maxNativeZoom: 16 },
  },
  {
    name: "Satellite",
    light: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
    dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
  },
  {
    name: "Topo",
    light: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
    dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
  },
  {
    name: "Hybrid",
    light: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
    dark: { url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", attribution: ATTR_NONE },
    overlays: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}",
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    ],
  },
  // ── TomTom styles (added Sep 2 2026) ──
  // Served through our own /api/tiles proxy so the TomTom key stays
  // server-side (same key api/view/traffic.ts uses). Appended at the end of
  // the catalogue so users' saved style indexes keep meaning the same map.
  {
    name: "Roads",
    light: { url: "/api/tiles?s=basic-main&z={z}&x={x}&y={y}", attribution: ATTR_NONE },
    dark: { url: "/api/tiles?s=basic-night&z={z}&x={x}&y={y}", attribution: ATTR_NONE },
  },
  {
    name: "Aerial",
    light: { url: "/api/tiles?s=sat&z={z}&x={x}&y={y}", attribution: ATTR_NONE, maxNativeZoom: 19 },
    dark: { url: "/api/tiles?s=sat&z={z}&x={x}&y={y}", attribution: ATTR_NONE, maxNativeZoom: 19 },
    overlays: ["/api/tiles?s=hybrid-main&z={z}&x={x}&y={y}"],
  },
];

// New users (no saved preference) open on the Hybrid satellite view.
export const DEFAULT_STYLE_INDEX = Math.max(0, MAP_STYLES.findIndex((s) => s.name === "Hybrid"));

// localStorage key persisting the main Scan Map's chosen style across sessions.
export const MAP_STYLE_STORAGE_KEY = "scanmap.mapStyle";

/** Persisted style-index state, clamped to the catalogue. Give each map its own key. */
export function useMapStylePref(storageKey: string): [number, (i: number) => void] {
  const [styleIndex, setStyleIndex] = useState<number>(() => {
    try {
      const saved = Number(localStorage.getItem(storageKey));
      return Number.isInteger(saved) && saved >= 0 && saved < MAP_STYLES.length ? saved : DEFAULT_STYLE_INDEX;
    } catch {
      return DEFAULT_STYLE_INDEX;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(styleIndex));
    } catch {
      /* ignore storage errors */
    }
  }, [storageKey, styleIndex]);
  return [styleIndex, setStyleIndex];
}

/** Renders the active style's base tiles plus any overlays. Use inside a MapContainer. */
export function MapTiles({ styleIndex, isDark }: { styleIndex: number; isDark: boolean }) {
  const style = MAP_STYLES[styleIndex] ?? MAP_STYLES[0];
  const tile = isDark ? style.dark : style.light;
  return (
    <>
      <TileLayer
        key={`${styleIndex}-${isDark ? "d" : "l"}`}
        url={tile.url}
        attribution={tile.attribution}
        maxZoom={19}
        maxNativeZoom={tile.maxNativeZoom ?? 19}
      />
      {style.overlays?.map((overlayUrl, i) => (
        <TileLayer key={`overlay-${styleIndex}-${i}`} url={overlayUrl} attribution="" />
      ))}
    </>
  );
}

/**
 * Basemap dropdown — the same Layers control the Scan Map uses. Styled with the
 * app's light/dark classes; the menu floats above Leaflet (z-[1200]).
 */
export function MapStylePicker({
  styleIndex,
  onChange,
  buttonClassName,
}: {
  styleIndex: number;
  onChange: (i: number) => void;
  buttonClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = MAP_STYLES[styleIndex] ?? MAP_STYLES[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors",
          "border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
          "dark:border-[#404040] dark:bg-[#2D2D2D] dark:text-gray-200 dark:hover:bg-[#242424]",
          buttonClassName
        )}
        title={`Map style: ${active.name}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Layers className="h-4 w-4" />
        {active.name}
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 z-[1200] mt-1 w-40 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-[#404040] dark:bg-[#2D2D2D]"
        >
          {MAP_STYLES.map((s, i) => {
            const selected = i === styleIndex;
            return (
              <button
                key={s.name}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(i);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-bold transition-colors",
                  selected
                    ? "bg-[#3B7BBE] text-white"
                    : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-[#3a3a3a]"
                )}
              >
                <Check className={cn("h-3.5 w-3.5 flex-shrink-0", !selected && "invisible")} style={{ color: selected ? "#DDAF41" : undefined }} />
                {s.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
